// Is a promo code waiting to be redeemed right after sign-in? True while
// sessionStorage holds 'ttt_event_code' (typed on the landing or login page)
// or while PendingCodeRedeemer has that request in flight. PublicEntryRoute
// and AuthCallback hold their redirect while this is true, so the redeemer
// gets to finish and route the person itself.
import { useSyncExternalStore } from 'react';
import { normalizePromoCode, promoDays } from '@/lib/promoCodes';

export const EVENT_CODE_KEY = 'ttt_event_code';

// How this sign-in started, for analytics only. Written where sign-in starts
// (password form, OAuth button, magic link), read once by the redeemer.
export const SIGNIN_METHOD_KEY = 'ttt_signin_method';
const SIGNIN_METHODS = new Set(['email_signin', 'oauth', 'magic_link']);

// An email link (magic link, sign-up confirmation) opens in a new tab, where
// sessionStorage starts empty. These query params carry what the old tab knew.
// Not 'code': supabase uses that one for its own exchange.
export const CODE_PENDING_PARAM = 'code_pending';
export const SIGNIN_METHOD_PARAM = 'signin_method';
const CODE_PENDING_SEEN_KEY = 'ttt_code_pending_seen';
const CALLBACK_PATH = '/auth/callback';

let inFlight = false;
const subs = new Set();
const emit = () => subs.forEach((f) => f());

const hasStoredCode = () => {
  try {
    return Boolean(sessionStorage.getItem(EVENT_CODE_KEY));
  } catch {
    return false;
  }
};

export const isPromoPending = () => inFlight || hasStoredCode();

export const setPromoInFlight = (value) => {
  inFlight = Boolean(value);
  emit();
};

export const isPromoInFlight = () => inFlight;

/** Take the stored code and clear it in one step, so it is used once. */
export const takeStoredCode = () => {
  try {
    const code = sessionStorage.getItem(EVENT_CODE_KEY);
    if (code) sessionStorage.removeItem(EVENT_CODE_KEY);
    return code || null;
  } catch {
    return null;
  }
};

export const notifyPromoChange = emit;

/** Keep an allowlisted code for the sign-in that follows. False if refused. */
export const storePendingCode = (code) => {
  const c = normalizePromoCode(code);
  if (!c || promoDays(c) === null) return false;
  try {
    sessionStorage.setItem(EVENT_CODE_KEY, c);
  } catch {
    return false;
  }
  emit();
  return true;
};

export const setSignInMethod = (method) => {
  if (!SIGNIN_METHODS.has(method)) return;
  try { sessionStorage.setItem(SIGNIN_METHOD_KEY, method); } catch { /* analytics only */ }
};

/**
 * Take the sign-in method once. With no hint, the page tells it the way the
 * old code did: /auth/callback was 'oauth', /login was 'email_signin'.
 */
export const takeSignInMethod = (pathname) => {
  let hint = null;
  try {
    hint = sessionStorage.getItem(SIGNIN_METHOD_KEY);
    if (hint) sessionStorage.removeItem(SIGNIN_METHOD_KEY);
  } catch { /* noop */ }
  if (SIGNIN_METHODS.has(hint)) return hint;
  if (pathname === CALLBACK_PATH) return 'oauth';
  if (pathname === '/login') return 'email_signin';
  return 'session';
};

/** emailRedirectTo for a link that may open in a new tab. */
export const emailCallbackUrl = (origin, method) => {
  // Only a link that carries a code gets a query string. Every other email link
  // stays the bare /auth/callback that Supabase's redirect allowlist already
  // accepts, so a stricter allowlist can only ever cost the code, never the login.
  const q = new URLSearchParams();
  let code = null;
  try { code = sessionStorage.getItem(EVENT_CODE_KEY); } catch { /* noop */ }
  if (code && promoDays(code) !== null) {
    q.set(CODE_PENDING_PARAM, normalizePromoCode(code));
    if (SIGNIN_METHODS.has(method)) q.set(SIGNIN_METHOD_PARAM, method);
  }
  const qs = q.toString();
  return `${origin}${CALLBACK_PATH}${qs ? `?${qs}` : ''}`;
};

/**
 * On /auth/callback, pick up what an email link carried. The code is checked
 * against the allowlist and taken once per tab, so reloading the link does not
 * queue it again.
 */
export const captureCallbackParams = (pathname, search) => {
  if (pathname !== CALLBACK_PATH || !search) return;
  let params;
  try { params = new URLSearchParams(search); } catch { return; }
  const method = params.get(SIGNIN_METHOD_PARAM);
  if (SIGNIN_METHODS.has(method)) setSignInMethod(method);
  const code = normalizePromoCode(params.get(CODE_PENDING_PARAM));
  if (!code || promoDays(code) === null) return;
  try {
    if (sessionStorage.getItem(CODE_PENDING_SEEN_KEY) === code) return;
    sessionStorage.setItem(CODE_PENDING_SEEN_KEY, code);
  } catch {
    return;
  }
  storePendingCode(code);
};

const subscribe = (f) => {
  subs.add(f);
  return () => subs.delete(f);
};

export const usePromoPending = () => useSyncExternalStore(subscribe, isPromoPending, () => false);
